import { useState, useMemo, useRef, useEffect } from 'react';
import { Building2, Plus, UserPlus, Check, ChevronDown, AlertTriangle, X, Shield, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import apiClient from '@/lib/api';

/**
 * Normalizes text for accent and case insensitive search (NFKD).
 */
export function normalizeText(text) {
  if (!text) return '';
  return text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’‘]/g, "'")
    .toLowerCase()
    .trim();
}

/**
 * Fuzzy check to detect potential duplicate company names.
 */
export function findSimilarCompanies(query, companies) {
  const normQuery = normalizeText(query);
  if (!normQuery || normQuery.length < 2) return [];

  return companies.filter((comp) => {
    const normName = normalizeText(comp.name);
    const normGroup = normalizeText(comp.group_name);
    const normAliases = (comp.aliases || []).map(normalizeText);

    // Exact normalized match or inclusion
    if (normName === normQuery) return true;
    if (normName.includes(normQuery) || normQuery.includes(normName)) return true;
    if (normGroup && (normGroup.includes(normQuery) || normQuery.includes(normGroup))) return true;
    if (normAliases.some((a) => a && (a.includes(normQuery) || normQuery.includes(a)))) return true;
    return false;
  });
}

const inputClass =
  'w-full min-h-11 rounded-lg bg-slate-900 border border-white/20 px-3 py-2 text-sm text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-400';

export default function CompanySelectCombobox({
  companies = [],
  selectedCompanyId = '',
  onSelectCompany,
  selectedContactId = '',
  onSelectContact,
  onCompanyCreated,
  onContactCreated,
  disabled = false,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const dropdownRef = useRef(null);

  // New Company Modal Form State
  const [newCompany, setNewCompany] = useState({
    name: '',
    group_name: '',
    aliases: '',
    sector: '',
    first_name: '',
    last_name: '',
    role: '',
    email: '',
    phone: '',
  });
  const [savingCompany, setSavingCompany] = useState(false);
  const [companyError, setCompanyError] = useState('');

  // New Contact Modal Form State
  const [newContact, setNewContact] = useState({
    first_name: '',
    last_name: '',
    role: '',
    email: '',
    phone: '',
  });
  const [savingContact, setSavingContact] = useState(false);
  const [contactError, setContactError] = useState('');

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedCompany = useMemo(() => {
    return companies.find((c) => c.id === selectedCompanyId || c._id === selectedCompanyId) || null;
  }, [companies, selectedCompanyId]);

  // Filter companies based on search term
  const filteredCompanies = useMemo(() => {
    const q = normalizeText(searchTerm);
    if (!q) return companies;
    return companies.filter((c) => {
      const matchName = normalizeText(c.name).includes(q);
      const matchGroup = normalizeText(c.group_name).includes(q);
      const matchSector = normalizeText(c.sector).includes(q);
      const matchAlias = (c.aliases || []).some((a) => normalizeText(a).includes(q));
      return matchName || matchGroup || matchSector || matchAlias;
    });
  }, [companies, searchTerm]);

  // Similar companies warning when typing in modal
  const potentialDuplicates = useMemo(() => {
    return findSimilarCompanies(newCompany.name, companies);
  }, [newCompany.name, companies]);

  // Contact list for selected company
  const companyContacts = useMemo(() => {
    if (!selectedCompany) return [];
    return Array.isArray(selectedCompany.contacts) ? selectedCompany.contacts : [];
  }, [selectedCompany]);

  const handleSelectCompany = (comp) => {
    const compId = comp.id || comp._id;
    onSelectCompany?.(compId, comp);
    setSearchTerm('');
    setIsOpen(false);

    // Auto-select first contact if exists and none selected
    const firstContact = comp.contacts?.[0];
    if (firstContact && (!selectedContactId || !comp.contacts.some((c) => (c.id || c._id) === selectedContactId))) {
      onSelectContact?.(firstContact.id || firstContact._id || '');
    } else if (!firstContact) {
      onSelectContact?.('');
    }
  };

  const handleClearSelection = () => {
    onSelectCompany?.('', null);
    onSelectContact?.('');
    setSearchTerm('');
  };

  // Submit new company (prospect)
  const handleSubmitNewCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.name.trim()) {
      setCompanyError("Renseignez le nom de l'entreprise.");
      return;
    }
    setSavingCompany(true);
    setCompanyError('');
    try {
      const contacts = [];
      const hasContact = newCompany.first_name.trim() || newCompany.last_name.trim();
      if (hasContact) {
        contacts.push({
          first_name: newCompany.first_name.trim() || undefined,
          last_name: newCompany.last_name.trim() || undefined,
          full_name: `${newCompany.first_name.trim()} ${newCompany.last_name.trim()}`.trim(),
          role: newCompany.role.trim() || 'Contact principal',
          email: newCompany.email.trim() || undefined,
          phone: newCompany.phone.trim() || undefined,
          is_primary: true,
        });
      }

      const aliasesList = newCompany.aliases
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      const payload = {
        name: newCompany.name.trim(),
        group_name: newCompany.group_name.trim() || undefined,
        aliases: aliasesList,
        sector: newCompany.sector.trim() || undefined,
        contacts,
      };

      const res = await apiClient.post('/recruitment/companies/prospects', payload);
      const created = res.data;

      onCompanyCreated?.(created);
      onSelectCompany?.(created.id || created._id, created);

      const createdContact = created.contacts?.[0];
      if (createdContact) {
        onSelectContact?.(createdContact.id || createdContact._id || '');
      }

      setShowCompanyModal(false);
      setNewCompany({
        name: '',
        group_name: '',
        aliases: '',
        sector: '',
        first_name: '',
        last_name: '',
        role: '',
        email: '',
        phone: '',
      });
    } catch (err) {
      setCompanyError(err?.response?.data?.detail || "Échec de la création de l'entreprise.");
    } finally {
      setSavingCompany(false);
    }
  };

  // Submit new contact to selected company
  const handleSubmitNewContact = async (e) => {
    e.preventDefault();
    if (!selectedCompany) return;
    if (!newContact.last_name.trim() && !newContact.first_name.trim()) {
      setContactError("Renseignez au moins le nom ou prénom de l'interlocuteur.");
      return;
    }
    setSavingContact(true);
    setContactError('');
    try {
      const compId = selectedCompany.id || selectedCompany._id;
      const payload = {
        first_name: newContact.first_name.trim() || undefined,
        last_name: newContact.last_name.trim() || undefined,
        full_name: `${newContact.first_name.trim()} ${newContact.last_name.trim()}`.trim(),
        role: newContact.role.trim() || 'Interlocuteur',
        email: newContact.email.trim() || undefined,
        phone: newContact.phone.trim() || undefined,
        is_primary: companyContacts.length === 0,
      };

      const res = await apiClient.post(`/recruitment/companies/${compId}/contacts`, payload);
      const created = res.data;

      onContactCreated?.(created, compId);
      onSelectContact?.(created.id || created._id);

      setShowContactModal(false);
      setNewContact({
        first_name: '',
        last_name: '',
        role: '',
        email: '',
        phone: '',
      });
    } catch (err) {
      setContactError(err?.response?.data?.detail || "Échec de l'ajout de l'interlocuteur.");
    } finally {
      setSavingContact(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="company-select-combobox">
      {/* 1. Company Selection Combobox */}
      <div className="relative" ref={dropdownRef}>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5 text-blue-400" />
            Entreprise (Client ou Prospect)
          </label>
          <button
            type="button"
            onClick={() => {
              setNewCompany((prev) => ({ ...prev, name: searchTerm }));
              setShowCompanyModal(true);
            }}
            disabled={disabled}
            className="text-[11px] font-semibold text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors disabled:opacity-50"
            data-testid="create-new-company-btn"
          >
            <Plus className="h-3 w-3" />
            + Créer une nouvelle entreprise
          </button>
        </div>

        {selectedCompany ? (
          /* Selected Company Badge / Card */
          <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900 border border-blue-500/40 text-xs shadow-inner">
            <div className="flex items-center gap-2.5">
              <div
                className={`p-1.5 rounded-lg ${
                  selectedCompany.company_type === 'CLIENT'
                    ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20'
                    : 'bg-amber-500/10 text-amber-300 border border-amber-500/20'
                }`}
              >
                {selectedCompany.company_type === 'CLIENT' ? (
                  <Shield className="h-4 w-4" />
                ) : (
                  <Clock className="h-4 w-4" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-white text-sm">{selectedCompany.name}</span>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                      selectedCompany.company_type === 'CLIENT'
                        ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20'
                        : 'bg-amber-500/10 text-amber-300 border-amber-500/20'
                    }`}
                  >
                    {selectedCompany.company_type === 'CLIENT' ? 'Client actif' : 'Prospect'}
                  </span>
                </div>
                {selectedCompany.group_name && (
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Groupe de rattachement : <strong className="text-slate-300">{selectedCompany.group_name}</strong>
                  </p>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={handleClearSelection}
              disabled={disabled}
              className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
              title="Changer d'entreprise"
              data-testid="clear-company-btn"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          /* Searchable Input with Dropdown */
          <div>
            <div className="relative">
              <input
                type="text"
                className={inputClass}
                placeholder="Rechercher une entreprise (ex: BPCE, Société Générale, etc.)..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                disabled={disabled}
                data-testid="company-search-input"
              />
              <ChevronDown className="h-4 w-4 absolute right-3 top-3.5 text-slate-400 pointer-events-none" />
            </div>

            {isOpen && (
              <div
                className="absolute z-50 w-full mt-1.5 bg-slate-900 border border-white/20 rounded-xl shadow-2xl max-h-60 overflow-y-auto divide-y divide-white/10"
                data-testid="company-dropdown-menu"
              >
                {filteredCompanies.length === 0 ? (
                  <div className="p-4 text-center">
                    <p className="text-xs text-slate-400 mb-2">Aucune entreprise trouvée pour « {searchTerm} »</p>
                    <button
                      type="button"
                      onClick={() => {
                        setNewCompany((prev) => ({ ...prev, name: searchTerm }));
                        setShowCompanyModal(true);
                        setIsOpen(false);
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-xs font-semibold text-white shadow-xs"
                      data-testid="create-inline-company-btn"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Créer l'entreprise « {searchTerm} »
                    </button>
                  </div>
                ) : (
                  <>
                    {filteredCompanies.map((comp) => {
                      const isClient = comp.company_type === 'CLIENT';
                      return (
                        <button
                          key={comp.id || comp._id}
                          type="button"
                          onClick={() => handleSelectCompany(comp)}
                          className="w-full text-left p-3 hover:bg-white/5 transition-colors flex items-center justify-between group"
                          data-testid={`company-item-${comp.id || comp._id}`}
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-white text-xs group-hover:text-blue-300">
                                {comp.name}
                              </span>
                              <span
                                className={`text-[9px] font-bold px-1.5 py-0.2 rounded-full border ${
                                  isClient
                                    ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20'
                                    : 'bg-amber-500/10 text-amber-300 border-amber-500/20'
                                }`}
                              >
                                {isClient ? 'Client actif' : 'Prospect'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                              {comp.group_name && <span>Groupe : {comp.group_name}</span>}
                              {comp.sector && <span>• {comp.sector}</span>}
                              {comp.contacts?.length > 0 && (
                                <span>• {comp.contacts.length} interlocuteur(s)</span>
                              )}
                            </div>
                          </div>
                          <Check className="h-4 w-4 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      );
                    })}
                    <div className="p-2 bg-slate-950/60 sticky bottom-0">
                      <button
                        type="button"
                        onClick={() => {
                          setNewCompany((prev) => ({ ...prev, name: searchTerm }));
                          setShowCompanyModal(true);
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/30 text-xs font-semibold transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        + Créer une nouvelle entreprise
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 2. Interlocuteur Section (Shown when company selected or available) */}
      {selectedCompany && (
        <div className="p-3.5 rounded-xl bg-slate-900/60 border border-white/10 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
              <UserPlus className="h-3.5 w-3.5 text-cyan-400" />
              Interlocuteur client
            </span>
            <button
              type="button"
              onClick={() => setShowContactModal(true)}
              disabled={disabled}
              className="text-[11px] font-semibold text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition-colors"
              data-testid="add-interlocutor-btn"
            >
              <Plus className="h-3 w-3" />
              + Ajouter un interlocuteur
            </button>
          </div>

          {companyContacts.length > 0 ? (
            <select
              className={inputClass}
              value={selectedContactId}
              onChange={(e) => onSelectContact?.(e.target.value)}
              disabled={disabled}
              data-testid="interlocutor-select"
            >
              <option value="">Sélectionner un interlocuteur (optionnel)</option>
              {companyContacts.map((ct) => {
                const ctId = ct.id || ct._id;
                const ctName = ct.full_name || ct.name || `${ct.first_name || ''} ${ct.last_name || ''}`.trim() || 'Sans nom';
                const details = [ct.role, ct.email, ct.phone].filter(Boolean).join(' • ');
                return (
                  <option key={ctId} value={ctId}>
                    {ctName} {details ? `(${details})` : ''}
                  </option>
                );
              })}
            </select>
          ) : (
            <div className="flex items-center justify-between p-2.5 rounded-lg bg-white/[0.03] border border-white/5 text-xs text-slate-400">
              <span>Aucun interlocuteur enregistré pour cette entreprise.</span>
              <button
                type="button"
                onClick={() => setShowContactModal(true)}
                disabled={disabled}
                className="px-2.5 py-1 rounded bg-cyan-600/20 text-cyan-300 border border-cyan-500/30 text-xs font-semibold hover:bg-cyan-600/30 transition-colors"
              >
                + Ajouter
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal A: Create New Company (Prospect) */}
      <Dialog open={showCompanyModal} onOpenChange={setShowCompanyModal}>
        <DialogContent className="max-w-xl bg-[#101426] text-white border-white/10 rounded-2xl w-[calc(100%_-_2rem)] max-h-[90vh] overflow-y-auto">
          <DialogTitle className="text-base font-bold flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-400" />
            Créer une nouvelle entreprise
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400">
            Ajoutez un nouveau prospect pour ce besoin. Il sera sauvegardé dans le référentiel sans affecter les clients actifs de Pilotage.
          </DialogDescription>

          <form onSubmit={handleSubmitNewCompany} className="space-y-4 pt-2">
            {companyError && (
              <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs">
                {companyError}
              </div>
            )}

            {/* Duplicate detection warning */}
            {potentialDuplicates.length > 0 && (
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs space-y-1.5" role="alert">
                <div className="flex items-center gap-1.5 font-bold">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />
                  <span>Correspondance proche détectée ({potentialDuplicates.length})</span>
                </div>
                <p className="text-[11px] leading-relaxed text-amber-200/90">
                  Des entreprises similaires existent déjà dans votre référentiel. S'il s'agit d'une entité juridique distincte (ex: filiale ou caisse régionale), vous pouvez poursuivre la création.
                </p>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {potentialDuplicates.map((sim) => (
                    <span
                      key={sim.id || sim._id}
                      className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-500/30"
                    >
                      {sim.name} {sim.group_name ? `(Groupe ${sim.group_name})` : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">
                  Nom de l'entreprise <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  className={inputClass}
                  placeholder="Ex: Banque Populaire Grand Ouest, BPCE..."
                  value={newCompany.name}
                  onChange={(e) => setNewCompany((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">
                    Groupe de rattachement (optionnel)
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    placeholder="Ex: BPCE, Crédit Agricole..."
                    value={newCompany.group_name}
                    onChange={(e) => setNewCompany((prev) => ({ ...prev, group_name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">
                    Alias éventuels (séparés par virgule)
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    placeholder="Ex: BPGO, Banque Pop"
                    value={newCompany.aliases}
                    onChange={(e) => setNewCompany((prev) => ({ ...prev, aliases: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">
                  Secteur d'activité (optionnel)
                </label>
                <input
                  type="text"
                  className={inputClass}
                  placeholder="Ex: Banque, Assurance, Retail..."
                  value={newCompany.sector}
                  onChange={(e) => setNewCompany((prev) => ({ ...prev, sector: e.target.value }))}
                />
              </div>

              <div className="pt-2 border-t border-white/10 space-y-3">
                <span className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                  Interlocuteur initial (optionnel)
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-slate-400 block mb-1">Prénom</label>
                    <input
                      type="text"
                      className={inputClass}
                      placeholder="Prénom"
                      value={newCompany.first_name}
                      onChange={(e) => setNewCompany((prev) => ({ ...prev, first_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-400 block mb-1">Nom</label>
                    <input
                      type="text"
                      className={inputClass}
                      placeholder="Nom"
                      value={newCompany.last_name}
                      onChange={(e) => setNewCompany((prev) => ({ ...prev, last_name: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-slate-400 block mb-1">Fonction / Rôle</label>
                    <input
                      type="text"
                      className={inputClass}
                      placeholder="Ex: Directeur de projet, RH..."
                      value={newCompany.role}
                      onChange={(e) => setNewCompany((prev) => ({ ...prev, role: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-400 block mb-1">Email</label>
                    <input
                      type="email"
                      className={inputClass}
                      placeholder="contact@entreprise.com"
                      value={newCompany.email}
                      onChange={(e) => setNewCompany((prev) => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-white/10">
              <button
                type="button"
                onClick={() => setShowCompanyModal(false)}
                className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-semibold text-slate-300"
                disabled={savingCompany}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={savingCompany || !newCompany.name.trim()}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-xs font-semibold text-white disabled:opacity-50 flex items-center gap-1.5"
                data-testid="submit-new-company-btn"
              >
                {savingCompany ? 'Création en cours...' : "Créer l'entreprise et sélectionner"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal B: Add Interlocutor to selected company */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="max-w-md bg-[#101426] text-white border-white/10 rounded-2xl w-[calc(100%_-_2rem)]">
          <DialogTitle className="text-base font-bold flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-cyan-400" />
            Ajouter un interlocuteur
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400">
            Rattachez un nouvel interlocuteur à l'entreprise « {selectedCompany?.name} ».
          </DialogDescription>

          <form onSubmit={handleSubmitNewContact} className="space-y-4 pt-2">
            {contactError && (
              <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs">
                {contactError}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">Prénom</label>
                <input
                  type="text"
                  className={inputClass}
                  placeholder="Prénom"
                  value={newContact.first_name}
                  onChange={(e) => setNewContact((prev) => ({ ...prev, first_name: e.target.value }))}
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">
                  Nom <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  className={inputClass}
                  placeholder="Nom"
                  value={newContact.last_name}
                  onChange={(e) => setNewContact((prev) => ({ ...prev, last_name: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Fonction / Rôle</label>
              <input
                type="text"
                className={inputClass}
                placeholder="Ex: Chef de projet, Acheteur prestations, DSI..."
                value={newContact.role}
                onChange={(e) => setNewContact((prev) => ({ ...prev, role: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">Email</label>
                <input
                  type="email"
                  className={inputClass}
                  placeholder="nom@entreprise.com"
                  value={newContact.email}
                  onChange={(e) => setNewContact((prev) => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">Téléphone</label>
                <input
                  type="tel"
                  className={inputClass}
                  placeholder="+33 6 12 34 56 78"
                  value={newContact.phone}
                  onChange={(e) => setNewContact((prev) => ({ ...prev, phone: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-white/10">
              <button
                type="button"
                onClick={() => setShowContactModal(false)}
                className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-semibold text-slate-300"
                disabled={savingContact}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={savingContact || (!newContact.first_name.trim() && !newContact.last_name.trim())}
                className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-xs font-semibold text-white disabled:opacity-50 flex items-center gap-1.5"
                data-testid="submit-new-contact-btn"
              >
                {savingContact ? 'Enregistrement...' : 'Ajouter et sélectionner'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
